export const APP_NAME = "Vice Valley HR Hub";
export const APP_FOOTER = "Vice Valley Roleplay • Human Resources";
export const BRAND_NAME = "Vice Valley Roleplay";
export const BRAND_LOGO = "https://img.vicevalleyroleplay.com/img/n06khfJd0B683-6KpPMeKmeK";
export const DEFAULT_ACCENT = "#E11D8A";
export const DEFAULT_GUILD_ID = "1389331641012584579";
export const DEFAULT_HR_ROLE_ID = "1389345964095180881";
export const DEFAULT_MANAGER_USER_ID = "999033836358873260";
export const AWAITING_TRAINING_ROLE_ID = "1390332362595766333";

export const ACTIVITY_TYPES = ["INTERVIEW", "TRAINING"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const CALCULATION_MODES = ["AND", "OR", "COMBINED"] as const;
export type CalculationMode = (typeof CALCULATION_MODES)[number];

export const REQUIREMENT_STATUSES = ["ACTIVE", "INACTIVE"] as const;
export type RequirementStatus = (typeof REQUIREMENT_STATUSES)[number];

export const COMPLETION_BUCKETS = [
  "EXCEEDED",
  "MET",
  "CLOSE",
  "BELOW",
] as const;
export type CompletionBucket = (typeof COMPLETION_BUCKETS)[number];

export const TREND_DIRECTIONS = ["UP", "DOWN", "STABLE"] as const;
export type TrendDirection = (typeof TREND_DIRECTIONS)[number];

export const MANAGER_ROLES = [
  "SUPER_ADMIN",
  "HR_ADMINISTRATOR",
  "HR_SUPERVISOR",
  "HR_MEMBER",
] as const;
export type ManagerRole = (typeof MANAGER_ROLES)[number];

export const PERMISSIONS = [
  "VIEW_DASHBOARD",
  "VIEW_ANALYTICS",
  "VIEW_ARCHIVE",
  "VIEW_AUDIT_LOGS",
  "VIEW_MEMBER_IDS",
  "MANAGE_ACTIVITY",
  "EDIT_ACTIVITY",
  "MANAGE_MEMBERS",
  "MANAGE_TEAMS",
  "MANAGE_REQUIREMENTS",
  "MANAGE_MANAGERS",
  "MANAGE_SETTINGS",
  "MANAGE_ANNOUNCEMENTS",
  "MANAGE_NOTES",
  "EXPORT_REPORTS",
  "RECALCULATE_STATS",
  "LOCK_PERIOD",
  "SYNC_DISCORD",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<ManagerRole, readonly Permission[]> = {
  SUPER_ADMIN: PERMISSIONS,
  HR_ADMINISTRATOR: [
    "VIEW_DASHBOARD",
    "VIEW_ANALYTICS",
    "VIEW_ARCHIVE",
    "VIEW_AUDIT_LOGS",
    "VIEW_MEMBER_IDS",
    "MANAGE_ACTIVITY",
    "EDIT_ACTIVITY",
    "MANAGE_MEMBERS",
    "MANAGE_TEAMS",
    "MANAGE_REQUIREMENTS",
    "MANAGE_SETTINGS",
    "MANAGE_ANNOUNCEMENTS",
    "MANAGE_NOTES",
    "EXPORT_REPORTS",
    "SYNC_DISCORD",
  ],
  HR_SUPERVISOR: [
    "VIEW_DASHBOARD",
    "VIEW_ANALYTICS",
    "VIEW_ARCHIVE",
    "VIEW_MEMBER_IDS",
    "MANAGE_ACTIVITY",
    "EXPORT_REPORTS",
  ],
  HR_MEMBER: ["VIEW_DASHBOARD"],
};

export const LEADERBOARD_VISIBILITY = ["PUBLIC", "ADMIN_ONLY", "DISABLED"] as const;
export type LeaderboardVisibility = (typeof LEADERBOARD_VISIBILITY)[number];

export const AUDIT_ACTIONS = [
  "AUTH_LOGIN",
  "AUTH_LOGOUT",
  "ACTIVITY_LOGGED",
  "ACTIVITY_EDITED",
  "ACTIVITY_VOIDED",
  "ACTIVITY_RESTORED",
  "MEMBER_ADDED",
  "MEMBER_REMOVED",
  "MEMBER_REACTIVATED",
  "MEMBER_REFRESHED",
  "TEAM_CREATED",
  "TEAM_UPDATED",
  "TEAM_DELETED",
  "REQUIREMENT_UPDATED",
  "MANAGER_ADDED",
  "MANAGER_UPDATED",
  "MANAGER_REMOVED",
  "SETTINGS_UPDATED",
  "STATS_RECALCULATED",
  "PERIOD_LOCKED",
  "PERIOD_UNLOCKED",
  "DISCORD_SYNC",
  "NOTE_CREATED",
  "NOTE_UPDATED",
  "ANNOUNCEMENT_CREATED",
  "ANNOUNCEMENT_UPDATED",
  "EXPORT_GENERATED",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const MILESTONES = [
  { id: "interviews_50", type: "INTERVIEW" as const, threshold: 50, label: "50 Interviews" },
  { id: "interviews_100", type: "INTERVIEW" as const, threshold: 100, label: "100 Interviews" },
  { id: "trainings_50", type: "TRAINING" as const, threshold: 50, label: "50 Trainings" },
  { id: "trainings_100", type: "TRAINING" as const, threshold: 100, label: "100 Trainings" },
  { id: "total_500", type: "TOTAL" as const, threshold: 500, label: "500 Total Activities" },
] as const;

export function hasPermission(
  role: ManagerRole | null | undefined,
  permission: Permission,
): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function getDisplayName(member: {
  nickname?: string | null;
  globalName?: string | null;
  username?: string | null;
}): string {
  return member.nickname || member.globalName || member.username || "Unknown Member";
}

export function discordAvatarUrl(
  discordId: string,
  avatar: string | null | undefined,
): string {
  if (avatar) {
    const ext = avatar.startsWith("a_") ? "gif" : "png";
    return `https://cdn.discordapp.com/avatars/${discordId}/${avatar}.${ext}?size=128`;
  }
  const index = Number(BigInt(discordId) >> 22n) % 6;
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
}

export function hexToDiscordColor(hex: string): number {
  const cleaned = hex.replace("#", "");
  const value = Number.parseInt(cleaned, 16);
  return Number.isFinite(value) ? value : 0xe11d8a;
}

export function activityNoun(type: ActivityType, quantity: number): string {
  if (type === "INTERVIEW") return quantity === 1 ? "Interview" : "Interviews";
  return quantity === 1 ? "Training" : "Trainings";
}

export function activityCountLabel(type: ActivityType, quantity: number): string {
  return `${quantity} ${activityNoun(type, quantity)}`;
}

export function mentionWithId(discordId: string): string {
  return `<@${discordId}> • ${discordId}`;
}
